import { message } from 'antd';
import { makeAutoObservable } from 'mobx';
import {
    IError,
    IPageable,
    IUser,
    IUserFiltres,
    IUserPost,
    IUserParams,
    IUsers,
    IRoles,
    IClubFilters,
} from 'models';
import { UserService } from 'services';

const usersFetchSignature = (pageable: IPageable, filter: IUserFiltres): string =>
    `${pageable.page}:${pageable.size}:${JSON.stringify(filter)}`;

const clubUsersFetchSignature = (pageable: IPageable, filter: IClubFilters): string =>
    `${pageable.page}:${pageable.size}:${JSON.stringify(filter)}`;

interface User {
    isLoadingUser: boolean;
    isLoadingUserSave: boolean;
    isLoadingActivated: boolean;
    errorUser: IError | null;
    successChangeStatus: boolean;
    successAdd: boolean;
    success: boolean;
    user: IUser | null;
    driverUser: IUserParams | null;
    users: IUsers | null;
    addUser: Function;
    getUser: Function;
    getUsers: Function;
    updateUser: Function;
}

class UserStore implements User {
    isLoadingUser = false;

    isLoadingUserSave = false;

    isLoadingActivated = false;

    errorUser: IError | null = null;

    errorClubUsers: IError | null = null;

    private usersRequestId = 0;

    private usersInFlightSig: string | null = null;

    private clubUsersRequestId = 0;

    private clubUsersInFlightSig: string | null = null;

    successChangeStatus = false;

    successAdd = false;

    success = false;

    user: IUser | null = null;

    driverUser: IUserParams | null = null;

    users: IUsers | null = null;

    roles: IRoles[] | null = null;

    clubUsers: IUsers | null = null;

    /** UI-состояние списка /club (сохраняется при переходе в карточку и обратно) */
    clubListSearch = '';

    clubListOrganization = '';

    clubListFilter: IClubFilters = {};

    clubListPageable: IPageable = { page: 0, size: 10 };

    /** UI-состояние списка /users */
    usersListSearch = '';

    usersListFilter: IUserFiltres = {};

    usersListPageable: IPageable = { page: 0, size: 10 };

    isLoadingClubUsers = false;

    isLoadingRoles = false;

    setIsLoading(bool: boolean) {
        this.isLoadingUser = bool;
    }

    setIsLoadingSave(bool: boolean) {
        this.isLoadingUserSave = bool;
    }

    setIsLoadingActivated(bool: boolean) {
        this.isLoadingActivated = bool;
    }

    setError(data: IError | null) {
        this.errorUser = data;
    }

    setClubError(data: IError | null) {
        this.errorClubUsers = data;
    }

    setClubUsers(data: IUsers | null) {
        this.clubUsers = data;
    }

    setClubListUi(search: string, organization: string, filter: IClubFilters, pageable: IPageable) {
        this.clubListSearch = search;
        this.clubListOrganization = organization;
        this.clubListFilter = filter;
        this.clubListPageable = pageable;
    }

    setUsersListUi(search: string, filter: IUserFiltres, pageable: IPageable) {
        this.usersListSearch = search;
        this.usersListFilter = filter;
        this.usersListPageable = pageable;
    }

    setSuccessChangeStatus(bool: boolean) {
        this.successChangeStatus = bool;
    }

    setSuccessAdd(bool: boolean) {
        this.successAdd = bool;
    }

    setSuccess(bool: boolean) {
        this.successAdd = bool;
    }

    setUser(data: IUser | null) {
        this.user = data;
    }

    setUsers(data: IUsers | null) {
        this.users = data;
    }

    setDriverUser(data: IUser | null) {
        this.driverUser = data;
    }

    setRoles(data: IRoles[] | null) {
        this.roles = data;
    }

    async addUser(data: IUserPost) {
        this.setIsLoadingSave(true);
        try {
            this.setError(null);
            await UserService.postUser(data);
            this.setSuccessAdd(true);
            message.success('Пользователь успешно создан!');
        } catch (error: any) {
            if (error && error?.response && error?.response?.data) {
                this.setError(error.response.data);
            }
        }
        this.setIsLoadingSave(false);
    }

    async deleteUser(id: string) {
        this.setIsLoading(true);
        try {
            this.setError(null);
            await UserService.deleteUser(id);
            this.setSuccessAdd(true);
            message.success('Пользователь успешно удалён!');
        } catch (error: any) {
            if (error && error?.response && error?.response?.data) {
                this.setError(error.response.data);
            }
        }
        this.setIsLoading(false);
    }

    async blockUser(id: string) {
        this.setIsLoading(true);
        try {
            this.setError(null);
            await UserService.blockUser(id);
            this.setSuccessAdd(true);
            message.success('Пользователь успешно заблокирован!');
        } catch (error: any) {
            if (error && error?.response && error?.response?.data) {
                this.setError(error.response.data);
            }
        }
        this.setIsLoading(false);
    }

    async unblockUser(id: string) {
        this.setIsLoading(true);
        try {
            this.setError(null);
            await UserService.unblockUser(id);
            this.setSuccessAdd(true);
            message.success('Пользователь успешно разблокирован!');
        } catch (error: any) {
            if (error && error?.response && error?.response?.data) {
                this.setError(error.response.data);
            }
        }
        this.setIsLoading(false);
    }

    async editUser(id: string, data: IUserPost) {
        this.setIsLoadingSave(true);
        try {
            this.setError(null);
            await UserService.putUser(id, data);
            this.setSuccessAdd(true);
            message.success('Пользователь успешно отредактирован!');
        } catch (error: any) {
            if (error && error?.response && error?.response?.data) {
                this.setError(error.response.data);
                if (error.response.data.status === 400) {
                    message.error('Неизвестная ошибка. Попробуйте позже');
                }
            }
        }
        this.setIsLoadingSave(false);
    }

    async getUser(id: string) {
        this.setIsLoading(true);
        try {
            const response = await UserService.getUser(id);
            this.setUser(response.data);
            this.setError(null);
        } catch (error: any) {
            if (error && error?.response && error?.response?.data) {
                this.setError(error.response.data);
            }
        }
        this.setIsLoading(false);
    }

    async getUsers(pageable: IPageable, filter?: IUserFiltres) {
        const activeFilter = filter ?? {};
        const fetchSig = usersFetchSignature(pageable, activeFilter);
        if (this.usersInFlightSig === fetchSig) {
            return;
        }
        this.usersInFlightSig = fetchSig;
        this.usersRequestId += 1;
        const requestId = this.usersRequestId;
        this.setIsLoading(true);

        try {
            const response = await UserService.getUsers(pageable, activeFilter);

            if (requestId !== this.usersRequestId) {
                return;
            }
            this.setUsers(response.data);
            this.setError(null);
        } catch (error: any) {
            if (requestId !== this.usersRequestId) {
                return;
            }
            if (error && error?.response && error?.response?.data) {
                this.setError(error.response.data);
            }
        } finally {
            if (requestId === this.usersRequestId) {
                this.setIsLoading(false);
                this.usersInFlightSig = null;
            }
        }
    }

    async getUserByParams(params: IUserParams) {
        this.setIsLoading(true);
        try {
            const response = await UserService.getUserByParams(params);
            this.setDriverUser(response.data);
            this.setError(null);
        } catch (error: any) {
            if (error && error?.response && error?.response?.data) {
                this.setError(error.response.data);
            }
        }
        this.setIsLoading(false);
    }

    async updateUser(id: string, subdivisionId: string) {
        this.setIsLoadingSave(true);
        try {
            const response = await UserService.updateUser(id, subdivisionId);
            this.setUser(response.data);
            this.setError(null);
        } catch (error: any) {
            if (error && error?.response && error?.response?.data) {
                this.setError(error.response.data);
                if (error.response.data.status === 400) {
                    message.error('Неизвестная ошибка. Попробуйте позже');
                }
            }
        }
        this.setIsLoadingSave(false);
    }

    async activateUser(id: string, reason: string) {
        this.setIsLoadingActivated(true);
        try {
            await UserService.activateUser(id, reason);
            this.setSuccessChangeStatus(true);
            this.setError(null);
        } catch (error: any) {
            if (error && error?.response && error?.response?.data) {
                this.setError(error.response.data);
                if (error.response.data.status === 400) {
                    message.error('Неизвестная ошибка. Попробуйте позже');
                }
            }
        }
        this.setIsLoadingActivated(false);
    }

    async deactivateUser(id: string, reason: string) {
        this.setIsLoadingActivated(true);
        try {
            await UserService.deactivateUser(id, reason);
            this.setError(null);
            this.setSuccessChangeStatus(true);
        } catch (error: any) {
            if (error && error?.response && error?.response?.data) {
                this.setError(error.response.data);
                if (error.response.data.status === 400) {
                    message.error('Неизвестная ошибка. Попробуйте позже');
                }
            }
        }
        this.setIsLoadingActivated(false);
    }

    async getClubUsers(pageable: IPageable, filter?: IClubFilters) {
        const activeFilter = filter ?? {};
        const fetchSig = clubUsersFetchSignature(pageable, activeFilter);
        if (this.clubUsersInFlightSig === fetchSig) {
            return;
        }
        this.clubUsersInFlightSig = fetchSig;
        this.clubUsersRequestId += 1;
        const requestId = this.clubUsersRequestId;
        this.isLoadingClubUsers = true;
        try {
            const response = await UserService.getClubUsers(pageable, activeFilter);
            if (requestId !== this.clubUsersRequestId) {
                return;
            }
            this.setClubUsers(response.data);
            this.setClubError(null);
        } catch (error: any) {
            if (requestId !== this.clubUsersRequestId) {
                return;
            }
            if (error && error?.response && error?.response?.data) {
                this.setClubError(error.response.data);
            }
        } finally {
            if (requestId === this.clubUsersRequestId) {
                this.isLoadingClubUsers = false;
                this.clubUsersInFlightSig = null;
            }
        }
    }

    async getRoles() {
        this.isLoadingRoles = true;
        try {
            this.setSuccess(false);
            const response = await UserService.getRoles();
            this.setRoles(response.data);
            this.setSuccess(true);
        } catch (error: any) {
            if (error && error?.response && error?.response?.data) {
                this.setError(error.response.data);
            }
            this.setSuccess(false);
        }
        this.isLoadingRoles = false;
    }

    constructor() {
        makeAutoObservable(this);
    }
}

const userStore = new UserStore();

export default userStore;
